import { Button } from '../../components/ui/button';
import { centsToDisplay } from '../../lib/money';
import type { Currency } from '../../types/account';

// Bidirectional budget prompt (spec §6.6). Fired when an AM changes a row's
// meta or google monthly budget and the new value differs from the Account's
// current value. Two outcomes:
//   - Permanent: patch the Account too (server call carries `patchAccount: true`)
//   - One-time:  leave the Account alone (`patchAccount: false`)
// Modal only appears on drift -- equal values skip it.

type Props = {
  platform: 'meta' | 'google';
  locationName: string;
  rolloutName: string;
  fromCents: number | null;
  toCents: number | null;
  currency: Currency;
  onPermanent: () => void;
  onOneTime: () => void;
  onCancel: () => void;
};

const PLATFORM_LABEL: Record<Props['platform'], string> = {
  meta: 'Meta Monthly Budget',
  google: 'Google Monthly Budget',
};

export function BudgetDriftModal({
  platform,
  locationName,
  rolloutName,
  fromCents,
  toCents,
  currency,
  onPermanent,
  onOneTime,
  onCancel,
}: Props) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-md bg-white shadow-xl">
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {PLATFORM_LABEL[platform]} changed
          </h2>
          <p className="text-sm text-slate-700">
            The {PLATFORM_LABEL[platform]} for <strong>{locationName}</strong> changed from{' '}
            <strong>{centsToDisplay(fromCents, currency)}</strong> to{' '}
            <strong>{centsToDisplay(toCents, currency)}</strong> on this rollout.
          </p>
          <p className="text-sm text-slate-600">
            Is this a <strong>permanent</strong> change to the location's retainer, or a{' '}
            <strong>one-time</strong> change for <em>{rolloutName}</em> only?
          </p>
          <div className="flex flex-col gap-2 pt-2">
            <Button onClick={onPermanent}>Permanent — update the Account</Button>
            <Button variant="outline" onClick={onOneTime}>
              One-time — this rollout only
            </Button>
            <button
              type="button"
              onClick={onCancel}
              className="text-sm text-slate-500 hover:text-slate-700 mt-2"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
